import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import {
  TRACK_COLORS,
  parseSegments,
  computeScreenPoints,
} from '../utils/trackRenderer';
import { findClosestSampleIndex } from '../utils/splitTimes';
import { speedToColor, getLegendGradientCSS } from '../utils/heatmapColors';
import { ALL_CHANNELS, getChannel } from '../utils/constants';
import { convertSpeed, speedUnit } from '../utils/units';

const TRACE_COLORS = [
  '#2e86c1', '#e74c3c', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
];

function drawCarIcon(ctx, cx, cy, angle, color, size = 12) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.7, -size * 0.5);
  ctx.lineTo(-size * 0.5, 0);
  ctx.lineTo(-size * 0.7, size * 0.5);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(size * 0.2, 0, size * 0.2, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, size + 4, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.4;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/**
 * Build per-coordinate colors for a single lap using the given data key.
 */
function buildLapColors(coordinates, lap, globalMin, globalMax, dataKey = 'speed') {
  const colors = [];
  for (let i = 0; i < coordinates.length; i++) {
    const coord = coordinates[i];
    if (coord.lat == null || coord.lng == null) {
      colors.push(TRACK_COLORS.trackBase);
      continue;
    }
    const match = findClosestSampleIndex(lap.data, coord.lat, coord.lng);
    if (match.index < 0) {
      colors.push(TRACK_COLORS.trackBase);
      continue;
    }
    const val = lap.data[match.index][dataKey] || 0;
    colors.push(speedToColor(val, globalMin, globalMax));
  }
  return colors;
}

export default function TrackHeatmap({ coordinates, laps, width = 700, carPositions, focusBounds, availableChannels = [], dataKey = 'speed', onDataKeyChange, user, mirrorX = false, mirrorY = false }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);

  // All selectable channels: speed + whatever's available in the session
  const selectableChannels = useMemo(() => {
    const speedCh = getChannel('speed', user);
    const others = availableChannels.length > 0 ? availableChannels : [];
    if (!speedCh) return others;
    return [speedCh, ...others.filter(ch => ch.key !== 'speed')];
  }, [availableChannels, user]);

  const activeChannel = selectableChannels.find(ch => ch.key === dataKey) || selectableChannels[0];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const sync = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setContainerWidth(rect.width);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const MAX_HEATMAP_WIDTH = 600;
  const renderWidth = Math.min(containerWidth || width, MAX_HEATMAP_WIDTH);

  // Compute height from track (or zoom bounds) aspect ratio
  const height = useMemo(() => {
    if (!coordinates?.length) return Math.floor(renderWidth * 0.6);
    let minLat, maxLat, minLng, maxLng;
    if (focusBounds) {
      ({ minLat, maxLat, minLng, maxLng } = focusBounds);
    } else {
      minLat = Infinity; maxLat = -Infinity; minLng = Infinity; maxLng = -Infinity;
      for (const p of coordinates) {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
      }
    }
    const latRange = maxLat - minLat || 0.001;
    const lngRange = maxLng - minLng || 0.001;
    const aspect = latRange / lngRange;
    const lapCount = laps?.length || 1;
    const ringExpansion = lapCount * 7;
    const padding = 80 + ringExpansion * 2;
    return Math.max(300, Math.round(renderWidth * aspect + padding));
  }, [coordinates, renderWidth, laps, focusBounds]);

  // Compute global value range across all selected laps for the active data key
  const globalValueRange = useMemo(() => {
    if (!laps?.length) return { min: 0, max: 100 };
    let min = Infinity, max = -Infinity;
    for (const lap of laps) {
      if (!lap?.data?.length) continue;
      for (const pt of lap.data) {
        const s = pt[dataKey] || 0;
        if (s < min) min = s;
        if (s > max) max = s;
      }
    }
    if (!isFinite(min)) return { min: 0, max: 100 };
    return { min, max };
  }, [laps, dataKey]);

  // Pre-compute colors for each lap
  const lapColorArrays = useMemo(() => {
    if (!coordinates?.length || !laps?.length) return [];
    return laps.map((lap) => {
      if (!lap?.data?.length) return null;
      return buildLapColors(coordinates, lap, globalValueRange.min, globalValueRange.max, dataKey);
    });
  }, [coordinates, laps, globalValueRange, dataKey]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !coordinates || coordinates.length < 2 || !laps?.length) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(renderWidth * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    canvas.style.height = `${height}px`;
    ctx.imageSmoothingEnabled = false;

    // Clear
    ctx.fillStyle = TRACK_COLORS.background;
    ctx.fillRect(0, 0, renderWidth, height);

    const lapCount = laps.length;
    const lineWidth = 4;
    // Each lap scales up by this factor from the centroid
    const scaleStep = 0.06;
    // Reserve padding so the outermost ring fits
    const maxScale = 1 + (lapCount - 1) * scaleStep;
    const basePadding = 40 + Math.ceil((maxScale - 1) * Math.max(renderWidth, height) * 0.5);

    const screenPts = computeScreenPoints(coordinates, renderWidth, height, basePadding, null, mirrorX, focusBounds, mirrorY);
    const segments = parseSegments(coordinates);

    // Compute centroid of the base track
    let cx = 0, cy = 0;
    for (const p of screenPts) { cx += p.x; cy += p.y; }
    cx /= screenPts.length;
    cy /= screenPts.length;

    // Draw a thin ghost of the base track for reference
    ctx.beginPath();
    ctx.moveTo(screenPts[0].x, screenPts[0].y);
    for (let i = 1; i < screenPts.length; i++) {
      ctx.lineTo(screenPts[i].x, screenPts[i].y);
    }
    ctx.strokeStyle = 'rgba(74, 106, 126, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Draw each lap as a scaled-up copy of the track from the centroid
    for (let li = 0; li < lapCount; li++) {
      const colors = lapColorArrays[li];
      if (!colors) continue;

      const scale = 1 + li * scaleStep;

      for (let i = 0; i < screenPts.length - 1; i++) {
        const x0 = cx + (screenPts[i].x - cx) * scale;
        const y0 = cy + (screenPts[i].y - cy) * scale;
        const x1 = cx + (screenPts[i + 1].x - cx) * scale;
        const y1 = cy + (screenPts[i + 1].y - cy) * scale;

        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.strokeStyle = colors[i];
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.stroke();
      }
    }

    // Draw segment tick marks from inner track to outermost ring
    const outerScale = 1 + (lapCount - 1) * scaleStep;
    const drawTick = (idx) => {
      if (idx <= 0 || idx >= screenPts.length - 1) return;
      const bx = screenPts[idx].x;
      const by = screenPts[idx].y;
      // Inner end: slightly inside the base track
      const innerScale = 0.97;
      const ix = cx + (bx - cx) * innerScale;
      const iy = cy + (by - cy) * innerScale;
      // Outer end: just past the outermost ring
      const outerPad = 1 + (outerScale - 1) * 1.1;
      const ox = cx + (bx - cx) * outerPad;
      const oy = cy + (by - cy) * outerPad;
      ctx.beginPath();
      ctx.moveTo(ix, iy);
      ctx.lineTo(ox, oy);
      ctx.strokeStyle = TRACK_COLORS.markerTick;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    };

    for (const seg of segments) {
      drawTick(seg.startIdx);
      drawTick(seg.endIdx);
    }

    // Draw segment labels on the inside of the base track
    for (const seg of segments) {
      if (!seg.label) continue;
      const midIdx = Math.floor((seg.startIdx + seg.endIdx) / 2);
      if (midIdx <= 0 || midIdx >= screenPts.length - 1) continue;

      // Place label inside the base track (toward centroid)
      const labelScale = 0.92;
      const lx = cx + (screenPts[midIdx].x - cx) * labelScale;
      const ly = cy + (screenPts[midIdx].y - cy) * labelScale;

      const fontSize = 10;
      ctx.font = `${fontSize}px "JetBrains Mono", "Fira Code", monospace`;
      const textW = ctx.measureText(seg.label).width;

      ctx.fillStyle = TRACK_COLORS.labelBg;
      ctx.beginPath();
      ctx.roundRect(lx - textW / 2 - 4, ly - fontSize / 2 - 3, textW + 8, fontSize + 6, 3);
      ctx.fill();

      ctx.fillStyle = TRACK_COLORS.labelText;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(seg.label, lx, ly);
    }

    // Start/finish marker on base track
    const sfSize = 14;
    const cells = 4;
    const cellSize = sfSize / cells;
    const sfX = screenPts[0].x - sfSize / 2;
    const sfY = screenPts[0].y - sfSize / 2;
    for (let r = 0; r < cells; r++) {
      for (let c = 0; c < cells; c++) {
        ctx.fillStyle = (r + c) % 2 === 0 ? '#FFFFFF' : '#000000';
        ctx.fillRect(sfX + c * cellSize, sfY + r * cellSize, cellSize, cellSize);
      }
    }

    // Draw car icons at crosshair positions
    if (carPositions && carPositions.length > 0) {
      let cMinLat, cMaxLat, cMinLng, cMaxLng;
      if (focusBounds) {
        ({ minLat: cMinLat, maxLat: cMaxLat, minLng: cMinLng, maxLng: cMaxLng } = focusBounds);
      } else {
        cMinLat = Infinity; cMaxLat = -Infinity; cMinLng = Infinity; cMaxLng = -Infinity;
        for (const p of coordinates) {
          if (p.lat < cMinLat) cMinLat = p.lat;
          if (p.lat > cMaxLat) cMaxLat = p.lat;
          if (p.lng < cMinLng) cMinLng = p.lng;
          if (p.lng > cMaxLng) cMaxLng = p.lng;
        }
      }
      const latRange = cMaxLat - cMinLat || 0.001;
      const lngRange = cMaxLng - cMinLng || 0.001;
      const vPad = Math.max(basePadding, height * 0.08);
      const availW = renderWidth - basePadding * 2;
      const availH = height - vPad * 2;
      const projScale = Math.min(availW / lngRange, availH / latRange);
      const projCx = renderWidth / 2;
      const projCy = height / 2;
      const midLat = (cMinLat + cMaxLat) / 2;
      const midLng = (cMinLng + cMaxLng) / 2;

      const projectGPS = (lat, lng) => ({
        x: (mirrorX ? 1 : -1) * (lng - midLng) * projScale + projCx,
        y: (mirrorY ? -1 : 1) * (lat - midLat) * projScale + projCy,
      });

      carPositions.forEach((car) => {
        if (car.lat == null || car.lng == null) return;
        const pos = projectGPS(car.lat, car.lng);
        let angle = 0;
        if (car.nextLat != null && car.nextLng != null) {
          const next = projectGPS(car.nextLat, car.nextLng);
          angle = Math.atan2(next.y - pos.y, next.x - pos.x);
        }
        drawCarIcon(ctx, pos.x, pos.y, angle, car.color || '#2e86c1', 10);
      });
    }
  }, [coordinates, laps, lapColorArrays, renderWidth, height, carPositions, focusBounds, mirrorX, mirrorY]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (!coordinates || coordinates.length < 2 || !laps?.length) {
    return (
      <div style={{
        width: '100%', height: 100,
        background: TRACK_COLORS.background,
        borderRadius: 8,
        border: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-text-dim)',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
      }}>
        No heatmap data available
      </div>
    );
  }

  const unit = activeChannel?.unit || '';
  const channelLabel = activeChannel?.label || 'Speed';

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      {/* Header with channel dropdown */}
      <div style={heatmapStyles.header}>
        <select
          data-doc-id="analysis-heatmap-channel"
          value={dataKey}
          onChange={(e) => onDataKeyChange?.(e.target.value)}
          style={heatmapStyles.dropdown}
        >
          {selectableChannels.map((ch) => (
            <option key={ch.key} value={ch.key}>
              {ch.label} Map{ch.unit ? ` (${ch.unit})` : ''}
            </option>
          ))}
        </select>
      </div>
      <canvas
            ref={canvasRef}
            style={{
              width: renderWidth,
              maxWidth: '100%',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              display: 'block',
            }}
          />
          {/* Legend */}
          <div style={{ marginTop: 8, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {/* Color scale */}
            <div style={{ flex: 1 }}>
              <div style={{
                height: 10,
                borderRadius: 4,
                background: getLegendGradientCSS('absolute'),
              }} />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 4,
                fontFamily: 'var(--font-body)',
                fontSize: 10,
                color: 'var(--color-text-dim)',
              }}>
                <span>{(dataKey === 'speed' ? convertSpeed(globalValueRange.min, user) : globalValueRange.min).toFixed(0)}{unit ? ` ${unit}` : ''}</span>
                <span style={{ fontSize: 9, letterSpacing: 0.5 }}>{channelLabel}</span>
                <span>{(dataKey === 'speed' ? convertSpeed(globalValueRange.max, user) : globalValueRange.max).toFixed(0)}{unit ? ` ${unit}` : ''}</span>
              </div>
            </div>
            {/* Lap legend */}
            <div style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              {laps.map((lap, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontFamily: 'var(--font-body)',
                  fontSize: 10,
                  color: 'var(--color-text-dim)',
                }}>
                  <span style={{
                    display: 'inline-block',
                    width: 14,
                    height: 3,
                    borderRadius: 1,
                    background: TRACE_COLORS[i % TRACE_COLORS.length],
                  }} />
                  L{lap.lapNum}{i === 0 ? ' (inner)' : ''}
                </div>
              ))}
            </div>
          </div>
    </div>
  );
}

const heatmapStyles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
  },
  dropdown: {
    background: '#0D1B2A',
    color: 'var(--color-text, #E0E0E0)',
    border: '1px solid rgba(138,180,200,0.25)',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 13,
    fontFamily: 'var(--font-heading, inherit)',
    cursor: 'pointer',
    outline: 'none',
  },
};
